import { UserProfile, UserRole } from "../services/userService";

// Pendo configuration interfaces following lowercase naming convention (Pendo requirement)
export interface PractitionerPendoVisitorProperties {
  id: string; // IdentityId from Properties
  v5userid?: string; // SourceKey.Id (USER-...)
  firstname?: string; // Properties.FirstName
  lastname?: string; // Properties.LastName
  displayname?: string; // DisplayName
  credential?: string | null; // Properties.Credential
  specialties?: string | null; // Properties.SpecialtyName
  upn?: string; // user.email (if available)
  version: string; // Fixed version
  associatedhsos?: string[]; // Scopes filtered for HSO- prefixed items
}

export interface PractitionerPendoAccountProperties {
  id: string; // Properties.PracticeId (ACCT-...)
  accountname?: string; // Properties.PracticeName
}

export interface PractitionerPendoConfig {
  visitor: PractitionerPendoVisitorProperties;
  account: PractitionerPendoAccountProperties;
}

export interface CareTeamPendoVisitorProperties {
  id: string; // IdentityId from Properties
  displayname?: string; // DisplayName from user profile
  firstname?: string; // Properties.FirstName from user profile
  lastname?: string; // Properties.LastName from user profile
  personalemail?: string; // user.email (if available)
  department?: string; // Properties.DepartmentName from role
  role?: string; // Properties.RoleName from role
}

export interface CareTeamPendoAccountProperties {
  id: string; // Properties.FacilityGuid from role
  facilityname?: string; // Properties.FacilityName from role
}

export interface CareTeamPendoConfig {
  visitor: CareTeamPendoVisitorProperties;
  account: CareTeamPendoAccountProperties;
}

export type PendoConfig = PractitionerPendoConfig | CareTeamPendoConfig;

/**
 * Helper function to check if a value is valid (not null, empty, or "null" string)
 */
function isValidValue(value: string | null | undefined): boolean {
  return !!(value && value !== "" && value !== "null");
}

/**
 * Create Practitioner Pendo configuration using byIdentityId data
 * Uses lowercase naming convention as required by Pendo
 */
function createPractitionerPendoConfig(user: UserProfile, selectedRole: UserRole): PractitionerPendoConfig {
  // Extract HSO-prefixed scopes for associatedhsos
  const associatedHSOs = selectedRole.scopes?.filter((scope) => scope.startsWith("HSO-")) || [];

  const config: PractitionerPendoConfig = {
    visitor: {
      id: user.uid, // IdentityId
      version: "3.0.4", // Fixed version
    },
    account: {
      id: selectedRole.facilityGuid || selectedRole.organizationId || "unknown", // PracticeId
    },
  };

  // Only include properties where we have actual data from byIdentityId

  // Add v5userid if we have it from the selected role's messaging key or role ID
  if (selectedRole.messagingKey?.Id) {
    config.visitor.v5userid = selectedRole.messagingKey.Id;
  }

  // Add names if available
  if (user.firstName) {
    config.visitor.firstname = user.firstName;
  }
  if (user.lastName) {
    config.visitor.lastname = user.lastName;
  }
  if (user.displayName) {
    config.visitor.displayname = user.displayName;
  }

  // Add credential if available
  config.visitor.credential = isValidValue(user.title) ? user.title : null;

  // Add specialty if available
  if (selectedRole && isValidValue(selectedRole.specialtyName)) {
    config.visitor.specialties = selectedRole.specialtyName || null;
  } else {
    config.visitor.specialties = null;
  }

  // Add upn if available from JWT token, otherwise fall back to email
  if (user.upn) {
    config.visitor.upn = user.upn;
  } else if (user.email && user.email !== "unknown@example.com") {
    config.visitor.upn = user.email;
  }

  // Add associatedhsos if we have any
  if (associatedHSOs.length > 0) {
    config.visitor.associatedhsos = associatedHSOs;
  }

  // Add account name if available
  if (selectedRole.practiceName || selectedRole.facilityName) {
    config.account.accountname = selectedRole.practiceName || selectedRole.facilityName;
  }

  return config;
}

/**
 * Create Care Team Pendo configuration using byIdentityId data
 * Uses lowercase naming convention as required by Pendo
 */
function createCareTeamPendoConfig(user: UserProfile, selectedRole: UserRole): CareTeamPendoConfig {
  const config: CareTeamPendoConfig = {
    visitor: {
      id: user.uid, // IdentityId
    },
    account: {
      id: selectedRole.facilityGuid || selectedRole.facilityId || "unknown", // FacilityGuid
    },
  };

  // Only include properties where we have actual data from byIdentityId

  // Add names if available
  if (user.displayName) {
    config.visitor.displayname = user.displayName;
  }
  if (user.firstName) {
    config.visitor.firstname = user.firstName;
  }
  if (user.lastName) {
    config.visitor.lastname = user.lastName;
  }

  // Add upn if available from JWT token, otherwise fall back to email
  if (user.upn) {
    config.visitor.personalemail = user.upn;
  } else if (user.email && user.email !== "unknown@example.com") {
    config.visitor.personalemail = user.email;
  }

  // Add department if available
  if (isValidValue(selectedRole?.departmentName)) {
    config.visitor.department = selectedRole.departmentName!;
  }

  // Add role if available
  if (isValidValue(selectedRole.roleName)) {
    config.visitor.role = selectedRole.roleName;
  }

  // Add facility name if available
  if (selectedRole.facilityName) {
    config.account.facilityname = selectedRole.facilityName;
  }

  return config;
}

/**
 * Converts shell UserRole and UserProfile to Pendo configuration format
 * Maps byIdentityId response data to proper Pendo configuration format
 * Only includes properties where we have actual data from the API response
 */
export function createPendoConfigFromByIdentityId(user: UserProfile, selectedRole: UserRole): PendoConfig {
  if (selectedRole.isPractitioner) {
    return createPractitionerPendoConfig(user, selectedRole);
  } else {
    return createCareTeamPendoConfig(user, selectedRole);
  }
}
